// hooks/usePageData.js
import { useState, useEffect, useCallback } from "react";
import { pagesApi } from "../api/ProfilePages";

// Hook for fetching a single page by slug
export const usePageData = (slug) => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPage = useCallback(async () => {
    if (!slug) {
      setError("Slug is required");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    const result = await pagesApi.getPageBySlug(slug);
    
    if (result.success) {
      setPageData(result.data);
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  }, [slug]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  return { pageData, loading, error, refetch: fetchPage };
};

// Hook for fetching multiple pages by slugs
export const useMultiplePages = (slugs) => {
  const [pagesData, setPagesData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPages = useCallback(async () => {
    if (!slugs || slugs.length === 0) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    const result = await pagesApi.getPagesBySlugs(slugs);
    
    if (result.success) {
      setPagesData(result.data);
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  }, [slugs]);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  return { pagesData, loading, error, refetch: fetchPages };
};

// Hook for fetching all pages with pagination
export const useAllPages = (page = 1, limit = 10, search = "") => {
  const [allPages, setAllPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);

  const fetchPages = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    const result = await pagesApi.getAllPages(page, limit, search);
    
    if (result.success) {
      setAllPages(result.data.pages || []);
      setPagination({
        total: result.data.total,
        page: result.data.page,
        limit: result.data.limit,
        totalPages: result.data.totalPages
      });
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  }, [page, limit, search]);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  return { allPages, loading, error, pagination, refetch: fetchPages };
};

// Example usage in components:
/*
// For single page (About, Contact, Terms)
import { usePageData } from '../hooks/usePageData';
const { pageData, loading, error } = usePageData('about-us');

// For multiple pages
import { useMultiplePages } from '../hooks/usePageData';
const { pagesData, loading, error } = useMultiplePages(['about-us', 'contact-us', 'terms-and-conditions']);

// For all pages with pagination
import { useAllPages } from '../hooks/usePageData';
const { allPages, loading, error, pagination } = useAllPages(1, 10, 'search term');
*/